import { useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import { mdiCheckCircleOutline } from '@mdi/js'

import { useAppDispatch } from 'app/hooks'
import { commonMessages } from 'app/i18n/messages'
import ConfirmModal from 'components/Modal/ConfirmModal'
import { TextField } from 'components/Fields'

import { newDomainThunk } from '../../slice'
import {
  AddDomainSuccessIcon,
  AddDomainSuccessIconWrap,
  NewDomainFormContent,
} from './styles'
import { newDomainModalMessages } from './messages'
import { DOMAIN_API_ERROR } from 'features/jobs/consts'
import { getDomainSubDomainsThunk } from 'components/DomainBreadcrumbs/slice'
import SuccessModal from 'components/Modal/SuccessModal'
import domainOrJobNameValidator from 'app/utils/domainOrJobNameValidator'

const NAME_FIELD = 'domainName'

interface NewDomainModalProps {
  domainId: string
  domainName: string
  onClose: () => void
}

const NewDomainModal = ({
  domainId,
  domainName,
  onClose,
}: NewDomainModalProps) => {
  const { formatMessage } = useIntl()
  const dispatch = useAppDispatch()

  const [isDomainCreated, setIsDomainCreated] = useState(false)

  const schema = Yup.object().shape({
    [NAME_FIELD]: domainOrJobNameValidator(formatMessage),
  })

  const initialValues = {
    domainName: '',
  }

  return isDomainCreated ? (
    <SuccessModal
      isOpen
      title={formatMessage(newDomainModalMessages.successTitle, {
        domainName,
      })}
      confirmBtnTitle={formatMessage(commonMessages.done)}
      onConfirm={onClose}
    >
      <div>
        <AddDomainSuccessIconWrap>
          <AddDomainSuccessIcon path={mdiCheckCircleOutline} size={4} />
        </AddDomainSuccessIconWrap>
        <FormattedMessage {...newDomainModalMessages.successText} />
      </div>
    </SuccessModal>
  ) : (
    <Formik
      initialValues={initialValues}
      validationSchema={schema}
      onSubmit={async ({ domainName }, formProps) => {
        try {
          await dispatch(
            newDomainThunk({ name: domainName, parentDomainId: domainId })
          )
          await dispatch(
            getDomainSubDomainsThunk({ domainId: domainId, forceUpdate: true })
          )
          // onClose()
          setIsDomainCreated(true)
        } catch (err: any) {
          console.log('Error adding new Domain/Group', err)

          if (err?.name === DOMAIN_API_ERROR.DOMAIN_JOB_EXISTS) {
            return formProps.setFieldError(
              NAME_FIELD,
              formatMessage(newDomainModalMessages.alreadyExists)
            )
          }
          return formProps.setFieldError(
            NAME_FIELD,
            formatMessage(commonMessages.commonError)
          )
        }
      }}
    >
      {(formikProps) => (
        <ConfirmModal
          isOpen
          onDiscard={onClose}
          title={
            <FormattedMessage
              {...newDomainModalMessages.title}
              values={{
                domainName,
              }}
            />
          }
          discardBtnTitle={<FormattedMessage {...commonMessages.cancel} />}
          confirmBtnTitle={
            <FormattedMessage {...newDomainModalMessages.ctaBtn} />
          }
          onConfirm={formikProps.submitForm}
          variant="primary"
          isConfirmBtnDisabled={
            formikProps.isSubmitting || !formikProps.isValid
          }
        >
          <Form>
            <NewDomainFormContent>
              <TextField
                name={NAME_FIELD}
                placeholder={formatMessage(newDomainModalMessages.input)}
              />
            </NewDomainFormContent>
          </Form>
        </ConfirmModal>
      )}
    </Formik>
  )
}

export default NewDomainModal
